import React, { createContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { Session, User, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase, refreshSession } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  profile: any | null;
  isLoading: boolean;
  error: Error | null;
  session: Session | null;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  userType: 'medico' | 'paciente' | 'assistente' | null;
  refreshAuth: () => Promise<void>;
  hasValidSession: boolean; // Added to track if session is valid
  isInitialized: boolean;   // Added to track initialization state
  isApproved: boolean;     // Added to track if doctor account is approved
  isSuperAdmin: boolean;   // Added to track if user is super admin
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  isLoading: true,
  error: null,
  session: null,
  userType: null,
  signOut: async () => {},
  refreshProfile: async () => {},
  refreshAuth: async () => {},
  hasValidSession: false,
  isInitialized: false,
  isApproved: false,
  isSuperAdmin: false,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [session, setSessionRaw] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [userType, setUserType] = useState<'medico' | 'paciente' | 'assistente' | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Create a ref to track the previous session for comparison
  const sessionRef = useRef<Session | null>(null);
  // Track last time the tab became visible
  const lastVisibilityChangeRef = useRef<number>(0);
  
  // Custom session setter with comparison logic to prevent unnecessary updates
  const setSession = useCallback((newSession: Session | null) => {
    // Prevent forced redraw on tab focus by comparing sessions
    // This fixes the issue with Supabase triggering SIGNED_IN events on tab focus
    // https://github.com/supabase/supabase/issues/7250
    
    // Compare relevant properties from the current and new session
    const currentUserId = sessionRef.current?.user?.id;
    const newUserId = newSession?.user?.id;
    const currentExpiry = sessionRef.current?.expires_at;
    const newExpiry = newSession?.expires_at;

    // Compare access tokens as well.  We observed that Supabase sometimes
    // re-emits a `SIGNED_IN` event (e.g. on `visibilitychange`) with an
    // identical `access_token` even when `expires_at` has been nudged. This
    // needlessly forces a rerender of the whole application, resetting the
    // Doctor Assessment flow.  If the user id and *access token* are
    // unchanged we treat the event as a no-op regardless of the expiry time.
    const currentAccessToken = sessionRef.current?.access_token;
    const newAccessToken = newSession?.access_token;

    // Check if this is coming from a tab focus event by inspecting metadata
    // @ts-ignore - isTabFocusEvent may not be in the type definitions but might be in metadata
    const isTabFocusEvent = newSession?.metadata?.isTabFocusEvent === true;

    // Skip session updates that match current user ID and access token
    // Also skip any updates with explicit tab focus events
    if ((currentUserId === newUserId &&
        newUserId !== undefined &&
        currentAccessToken === newAccessToken) || isTabFocusEvent) {
      console.log('AuthContext: Session update skipped - same user/token or tab focus event');
      return;
    }
    
    console.log('AuthContext: Session updated with new data');
    sessionRef.current = newSession;
    setSessionRaw(newSession);
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      console.log(`Fetching profile for user: ${userId}`);
      
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) {
        console.error("Profile fetch error:", profileError);
        throw profileError;
      }

      console.log("Profile data retrieved:", profileData);
      setProfile(profileData);
      
      // Determine super-admin status without invoking RPCs that may trigger
      // recursive RLS policies.  We rely on the `role` column that already
      // exists in `public.profiles`.
      const userIsSuperAdmin = profileData?.role === 'super_admin';
      setIsSuperAdmin(!!userIsSuperAdmin);
      console.log(`User super admin status: ${userIsSuperAdmin ? 'Yes' : 'No'}`);
      
      // Set user type based on the profile data
      if (profileData?.user_type) {
        setUserType(profileData.user_type as 'medico' | 'paciente' | 'assistente');
        console.log(`User type set to: ${profileData.user_type}`);
        
        // Check approval status for doctor and assistente accounts
        if (profileData.user_type === 'medico' || profileData.user_type === 'assistente') {
          const isAccountApproved = profileData.approval_status === 'APPROVED';
          setIsApproved(isAccountApproved);
          console.log(`${profileData.user_type} approval status: ${isAccountApproved ? 'Approved' : 'Pending'}`);

          // Immediately sign the doctor/assistente out if the account is not yet approved.
          // This guarantees the user cannot navigate the application until an
          // administrator explicitly approves the account **and** (optionally)
          // triggers the email‑confirmation/invitation flow.
          if (!isAccountApproved) {
            console.log(`${profileData.user_type} account pending approval – signing out`);
            await supabase.auth.signOut();
            setUser(undefined);
            setHasValidSession(false);
          }
        } else {
          // Patients are automatically approved
          setIsApproved(true);
        }
      }
    } catch (err: any) {
      console.error("Profile fetch error:", err.message);
      setError(err);
    }
  };

  // Refresh profile function to be exposed in the context
  const refreshProfile = async () => {
    if (!user) return;
    await fetchProfile(user.id);
  };

  // Function to manually refresh authentication state
  const refreshAuth = async () => {
    setIsLoading(true);
    try {
      const result = await refreshSession();
      
      if (result.success && result.session) {
        // Use our custom session setter with comparison logic
        setSession(result.session);
        setUser(result.session.user);
        setHasValidSession(true);
        
        if (result.session.user) {
          await fetchProfile(result.session.user.id);
        }
      } else {
        // If refresh failed, clear state
        setUser(null);
        setProfile(null);
        // Use our custom session setter
        setSession(null);
        setUserType(null);
        setHasValidSession(false);
      }
    } catch (err: any) {
      console.error("Auth refresh error:", err.message);
      setError(err);
      setHasValidSession(false);
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    setIsLoading(true);
    try {
      console.log("Signing out user...");
      
      // Clear state immediately to prevent race conditions
      setUser(null);
      setProfile(null);
      setSession(null);
      setUserType(null);
      setHasValidSession(false);
      setIsApproved(false);
      setIsSuperAdmin(false);
      setError(null);
      
      // Clear session ref
      sessionRef.current = null;
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) {
        console.warn("Supabase sign out error (continuing anyway):", error.message);
        // Don't throw error here - we want to continue with cleanup
      }
      
      // Clear local storage items
      window.localStorage.removeItem('ortho-care-auth-token');
      
      // Clear any other auth-related items in local storage
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('supabase') || key.includes('auth') || key.includes('token'))) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      console.log("User signed out successfully", { keysRemoved: keysToRemove });
      
    } catch (err: any) {
      console.error("Sign Out Error:", err.message);
      // Even if there's an error, ensure state is cleared
      setUser(null);
      setProfile(null);
      setSession(null);
      setUserType(null);
      setHasValidSession(false);
      setIsApproved(false);
      setIsSuperAdmin(false);
      setError(null);
      sessionRef.current = null;
    } finally {
      setIsLoading(false);
    }
  };

  // Track tab visibility changes to filter spurious SIGNED_IN events
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        lastVisibilityChangeRef.current = Date.now();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  useEffect(() => {
    console.log("AuthProvider initialized, checking session...");
    
    const initializeAuth = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Check if we're in a password reset or magic link flow
        const isPasswordResetFlow = window.location.href.includes('auth/resetPassword') || 
                                   window.location.href.includes('type=recovery') ||
                                   window.location.hash.includes('type=recovery');
        const isMagicLinkFlow = window.location.href.includes('auth/magiclink');
        
        if (isPasswordResetFlow || isMagicLinkFlow) {
          console.log(`Detected special auth flow: ${isPasswordResetFlow ? 'Password Reset' : 'Magic Link'}`);
        }
        
        // Get the initial session
        const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw sessionError;
        }

        console.log("Initial session check:", initialSession ? "Session found" : "No session");
        
        if (initialSession?.user) {
          // Initialize our session ref for later comparisons
          sessionRef.current = initialSession;
          // Use our custom session setter
          setSession(initialSession);
          setUser(initialSession.user);
          setHasValidSession(true);
          await fetchProfile(initialSession.user.id);
        } else if (isPasswordResetFlow || isMagicLinkFlow) {
          // For password reset or magic link flows, we may not have a session yet,
          // but we should still set initialized to true so auth changes are processed
          console.log("No session found, but in special auth flow. Continuing to listen for auth changes.");
          
          // If it's a password reset flow, check if we need to redirect
          if (isPasswordResetFlow && !window.location.pathname.includes('/reset-password')) {
            console.log("Redirecting to reset-password page");
            window.location.href = '/reset-password' + window.location.search + window.location.hash;
            return;
          }
        }
      } catch (err: any) {
        console.error("Session initialization error:", err.message);
        setError(err);
        setHasValidSession(false);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();

    // Set up auth state change subscription
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      console.log("Auth state change event:", event, {
        userId: currentSession?.user?.id,
        timestamp: new Date().toISOString(),
      });

      const recentlyVisible = Date.now() - lastVisibilityChangeRef.current < 500;
      if (event === 'SIGNED_IN' && recentlyVisible) {
        console.log('Skipping auth state change - tab focus triggered');
        lastVisibilityChangeRef.current = 0;
        return;
      }
      
      // Enhanced check for tab focus events using extra metadata provided by Supabase
      // @ts-ignore - isTabFocusEvent may not be in the type definitions but is provided by Supabase
      const isExplicitTabFocusEvent = currentSession?.metadata?.isTabFocusEvent === true;
      
      // Current check for tab focus events by comparing session data
      const currentUserId = sessionRef.current?.user?.id;
      const newUserId = currentSession?.user?.id;
      const currentAccessToken = sessionRef.current?.access_token;
      const newAccessToken = currentSession?.access_token;
      
      // Combined check: either explicitly marked as tab focus or looks like tab focus based on our heuristic
      const isTabFocusEvent = 
        isExplicitTabFocusEvent ||
        (event === 'SIGNED_IN' && 
        currentUserId === newUserId && 
        currentAccessToken === newAccessToken &&
        currentUserId !== undefined &&
        !window.location.href.includes('auth/resetPassword') && // Don't skip password reset flows
        !window.location.href.includes('auth/magiclink')); // Don't skip magic link flows
      
      // Skip processing if this is a tab focus event
      if (isTabFocusEvent) {
        console.log("Skipping auth state change - detected tab focus event");
        return;
      }
      
      // Handle PASSWORD_RECOVERY event explicitly
      if (event === 'PASSWORD_RECOVERY') {
        console.log("PASSWORD_RECOVERY event detected");
        
        // If not already on the reset-password page, redirect
        if (!window.location.pathname.includes('/reset-password')) {
          console.log("Redirecting to reset password page...");
          window.location.href = '/reset-password' + window.location.search + window.location.hash;
          return;
        }
      }
      
      // Only handle events if the auth provider has completed its initialization
      if (!isInitialized && event !== 'INITIAL_SESSION') {
        console.log("Skipping auth state change event while initializing:", event);
        return;
      }
      
      if (event === 'SIGNED_IN' && currentSession?.user) {
        console.log("User signed in:", currentSession.user.id);
        
        // Check if this is an actual new sign-in or just a token refresh
        const isActualSignIn = !user || user.id !== currentSession.user.id;
        
        // For magic link/password reset flows, we want to ensure the session is properly set
        const isMagicLinkOrResetFlow = 
          window.location.href.includes('auth/resetPassword') || 
          window.location.href.includes('auth/magiclink');
          
        if (isMagicLinkOrResetFlow) {
          console.log("Processing auth for magic link or password reset flow");
        }
        
        // Update session state using our custom setter with comparison
        setSession(currentSession);
        setUser(currentSession.user);
        setHasValidSession(true);
        
        // Only fetch profile on actual sign in or for magic link/reset flows
        if (isActualSignIn || isMagicLinkOrResetFlow) {
          await fetchProfile(currentSession.user.id);
        }
      } else if (event === 'SIGNED_OUT') {
        console.log("User signed out event detected");
        // Clear all auth state
        setUser(null);
        setProfile(null);
        setSession(null);
        setUserType(null);
        setHasValidSession(false);
        setIsApproved(false);
        setIsSuperAdmin(false);
        setError(null);
        sessionRef.current = null;
      } else if (event === 'TOKEN_REFRESHED' && currentSession) {
        console.log("Token refreshed for user:", currentSession.user.id);
        // IMPORTANT: Do NOT call setSession() here!
        // Token refresh doesn't change the user - only updates the access token.
        // Calling setSession() would trigger a context re-render, causing:
        // - All consumers to re-render
        // - Hooks to re-initialize (losing React state)
        // - Assessment data to be lost
        //
        // Instead, only update the internal sessionRef for comparison purposes.
        // The actual session object in state still works - just with an older token.
        // When we actually need a fresh token (e.g., for API calls), Supabase
        // handles that automatically via the stored session.
        sessionRef.current = currentSession;
        setHasValidSession(true);
        console.log("Token refresh handled - ref updated, state preserved");
      } else if (event === 'USER_UPDATED' && currentSession) {
        console.log("User updated:", currentSession.user.id);
        setUser(currentSession.user);
        setHasValidSession(true);
        await fetchProfile(currentSession.user.id);
      }
    });

    // Cleanup subscription on unmount
    return () => {
      console.log("Cleaning up auth subscription");
      subscription?.unsubscribe();
    };
  }, [isInitialized, setSession]);

  const value = {
    user,
    profile,
    isLoading,
    error,
    session,
    userType,
    signOut,
    refreshProfile,
    refreshAuth,
    hasValidSession,
    isInitialized,
    isApproved,
    isSuperAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

